"use client";

import { useCallback } from 'react';
import { useHistory } from '@/contexts/HistoryContext';
import { ToolInfo } from '@/contexts/ToolsContext';
import { generateImageId } from '@/utils/imageUtils';
import { ChatMessage } from '@/types/history';

// Higher-level hook for editor-specific history operations
export function useEditorHistory() {
  const history = useHistory();

  // Track when a photo is uploaded (initial state)
  const trackPhotoUpload = useCallback((imageData: string, imageId?: string) => {
    const finalImageId = imageId || generateImageId(imageData);
    history.addAction({
      type: 'photo_uploaded',
      imageId: finalImageId,
      imageData,
    });
  }, [history]);

  // Track when a photo is updated/processed by a tool
  const trackPhotoUpdate = useCallback((
    imageData: string,
    toolInfo: ToolInfo,
    imageId?: string,
    chatState?: ChatMessage[]
  ) => {
    const finalImageId = imageId || generateImageId(imageData);
    history.addAction({
      type: 'photo_updated',
      toolId: toolInfo.id,
      toolName: toolInfo.title,
      imageId: finalImageId,
      imageData,
      chatState,
    });
  }, [history]);

  // Undo and return the previous image
  const undoAndGetImage = useCallback((): string | null => {
    const previousAction = history.undo();
    return previousAction ? previousAction.imageData : null;
  }, [history]);

  // Redo and return the next image
  const redoAndGetImage = useCallback((): string | null => {
    const nextAction = history.redo();
    return nextAction ? nextAction.imageData : null;
  }, [history]);

  // Get current image ID
  const getCurrentImageId = useCallback((): string | null => {
    if (history.history.currentIndex < 0) return null;
    const currentAction = history.history.actions[history.history.currentIndex];
    return currentAction?.imageId || null;
  }, [history.history.actions, history.history.currentIndex]);

  // Get image ID for a specific history index
  const getImageIdAtIndex = useCallback((index: number): string | null => {
    if (index < 0 || index >= history.history.actions.length) return null;
    return history.history.actions[index]?.imageId || null;
  }, [history.history.actions]);

  return {
    // Basic history operations
    ...history,

    // Editor-specific tracking methods
    trackPhotoUpload,
    trackPhotoUpdate,

    // Enhanced undo/redo with image return
    undoAndGetImage,
    redoAndGetImage,

    // Image ID helpers
    getCurrentImageId,
    getImageIdAtIndex,
  };
}
